import { useState, useEffect, useCallback, useRef } from 'react';
import type { Module } from '@/types/modules';
import type { ReportConfig, ReportTheme } from '@/types/report';
import { getTheme } from './themes';
import { ReportSection } from './ReportSection';
import { ReportStatusBar } from './ReportStatusBar';
import { useSession } from '@/features/auth/hooks/useSession';
import { getActiveWorkspaceId } from '@/lib/api/workspace-context';
import { api } from '@/lib/api';
import { publishReport } from '@/lib/api/endpoints/workspaces';
import { useToast } from '@/app/providers/ToastContext';
import { useReportRunWatch } from '@/modules/pages/report/useReportRunWatch';
import './report.css';
import reportCssRaw from './report.css?raw';

interface Props {
  module: Module;
}

export function ReportModule({ module }: Props) {
  // Maintain local config state so we can receive real-time updates
  const [liveConfig, setLiveConfig] = useState<ReportConfig | undefined>(
    module.config as ReportConfig | undefined,
  );
  const [showRepublishPrompt, setShowRepublishPrompt] = useState(false);
  const [themePickerOpen, setThemePickerOpen] = useState(false);

  const { token, selectedContainer } = useSession();
  const { showToast } = useToast();

  // Sync from props when module prop changes (e.g., page navigation)
  useEffect(() => {
    setLiveConfig(module.config as ReportConfig | undefined);
  }, [module.id, module.config]);

  // Listen for streaming config updates from useChat
  useEffect(() => {
    const handler = (event: CustomEvent) => {
      const { moduleId, config } = event.detail || {};
      if (moduleId === module.id && config) {
        setLiveConfig(config as ReportConfig);
      }
    };
    window.addEventListener('xavior:report-config-update', handler as EventListener);
    return () => window.removeEventListener('xavior:report-config-update', handler as EventListener);
  }, [module.id]);

  // Listen for theme picker open from ModuleMenu "Change Theme" item
  useEffect(() => {
    const handler = (event: CustomEvent) => {
      if (event.detail?.moduleId === module.id) {
        setThemePickerOpen(true);
      }
    };
    window.addEventListener('xavior:open-theme-picker', handler as EventListener);
    return () => window.removeEventListener('xavior:open-theme-picker', handler as EventListener);
  }, [module.id]);

  // Watch background build progress (if this report has an active run)
  const workspaceId = selectedContainer?.id ?? selectedContainer?.ownerId ?? getActiveWorkspaceId();
  const runId = liveConfig?.runId || null;
  useReportRunWatch({
    runId,
    workspaceId: workspaceId || '',
    authToken: token ?? null,
    onProgress: useCallback((event: { eventType: string; data: Record<string, unknown> }) => {
      if (event.eventType === 'status' && event.data?.message) {
        setLiveConfig(prev => prev ? { ...prev, statusMessage: event.data.message as string } : prev);
      }
    }, []),
    onComplete: useCallback(() => {
      setLiveConfig(prev => prev ? { ...prev, status: 'complete', statusMessage: undefined, runId: undefined } : prev);
    }, []),
    onError: useCallback((error: string) => {
      setLiveConfig(prev => prev ? { ...prev, status: 'error', statusMessage: error } : prev);
    }, []),
  });

  const handlePublished = useCallback((slug: string, url: string) => {
    setLiveConfig(prev => prev ? { ...prev, publish: { slug, url, visibility: 'public', publishedAt: new Date().toISOString(), viewCount: 0 } } : prev);
  }, []);

  const handleUnpublished = useCallback(() => {
    setLiveConfig(prev => prev ? { ...prev, publish: undefined } : prev);
  }, []);

  const handleRepublish = useCallback(async () => {
    const workspaceId = selectedContainer?.id ?? selectedContainer?.ownerId ?? getActiveWorkspaceId();
    try {
      const result = await publishReport(token ?? null, workspaceId, module.id);
      setLiveConfig(prev => prev ? {
        ...prev,
        publish: { slug: result.slug, url: result.url, visibility: 'public', publishedAt: new Date().toISOString(), viewCount: prev.publish?.viewCount ?? 0 },
      } : prev);
      showToast('Live version updated', 'success');
    } catch (err) {
      console.error('Failed to republish:', err);
      showToast('Failed to update live version', 'error');
    }
  }, [token, selectedContainer, module.id, showToast]);

  const handleThemeChange = useCallback(async (newTheme: ReportTheme) => {
    const workspaceId = selectedContainer?.id ?? selectedContainer?.ownerId ?? getActiveWorkspaceId();
    const prev = liveConfig;
    if (!prev) return;

    const updatedConfig = { ...prev, theme: newTheme };
    const wasPublished = Boolean(prev.publish?.url);
    setLiveConfig(updatedConfig);

    // Persist to server
    try {
      await api.workspaces.updateWorkspaceModule(token ?? null, workspaceId, module.id, {
        config: updatedConfig,
      });
    } catch (err) {
      console.error('Failed to save theme:', err);
    }

    // If published, show republish prompt
    if (wasPublished) {
      setShowRepublishPrompt(true);
    }
  }, [liveConfig, module.id, token, selectedContainer]);

  const reportRef = useRef<HTMLDivElement>(null);

  const handleDownload = useCallback(() => {
    const container = reportRef.current?.querySelector('.report-container');
    if (!container) return;
    const currentTheme = getTheme(liveConfig?.theme ?? 'formal');
    const cssVars = Object.entries(currentTheme.variables)
      .map(([k, v]) => `${k}: ${v};`)
      .join('\n    ');

    const fontsLink = currentTheme.fontsUrl
      ? `<link rel="stylesheet" href="${currentTheme.fontsUrl}" />`
      : '';
    const title = module.label || 'Report';
    const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${title}</title>
  ${fontsLink}
  <style>
    :root {
    ${cssVars}
    }
    body { margin: 0; background: var(--report-bg, #fff); color: var(--report-text, #1a1a1a); }
    ${reportCssRaw}
    .report-section { opacity: 1; transform: none; }
  </style>
</head>
<body>
  <div class="report-container">
    ${container.innerHTML}
  </div>
</body>
</html>`;

    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title}.html`;
    a.click();
    URL.revokeObjectURL(url);
  }, [liveConfig, module.label]);

  const config = liveConfig;
  // Theme must be computed unconditionally so the useEffect below stays in
  // the same hook position on every render — Rules of Hooks. When config is
  // missing we fall through to the "No report data" branch below, but the
  // hook above must still see a stable reference (null is fine).
  const theme = config ? getTheme(config.theme ?? 'formal') : null;
  const sortedSections = config
    ? [...(Array.isArray(config.sections) ? config.sections : [])].sort((a, b) => a.order - b.order)
    : [];

  // Propagate theme CSS vars to ancestor so the header matches the report theme.
  // In pane mode the ancestor is .chat-module-pane-card; in inline mode it's .module-renderer-wrapper.
  // MUST sit above any early-returns below — moving it under a conditional return
  // crashes with "Rendered more hooks than during the previous render" the moment
  // config flips from undefined to defined mid-stream.
  useEffect(() => {
    if (!theme) return;
    const card = reportRef.current?.closest('.chat-module-pane-card')
              || reportRef.current?.closest('.module-renderer-wrapper');
    if (!card || !(card instanceof HTMLElement)) return;
    const vars = theme.variables as Record<string, string>;
    for (const [key, value] of Object.entries(vars)) {
      card.style.setProperty(key, value);
    }
    return () => {
      for (const key of Object.keys(vars)) {
        card.style.removeProperty(key);
      }
    };
  }, [theme]);

  if (!config || !theme) return <div className="module-content">No report data</div>;

  // Pulsing build loader — mirrors HtmlViewerModule's
  // build skeleton (same `.xavior-build-loader` styles already live in
  // report.css). Shown when the report has no sections yet AND either has
  // an active build run, an explicit queued/building status, or was created
  // within the last 30s (heuristic: agent is probably about to stream
  // sections in). Without this, freshly-created reports rendered as a
  // blank pane until the first section landed, which is jarring compared
  // to the page-build flow.
  const moduleAgeMs = module.createdAt
    ? Date.now() - new Date(module.createdAt).getTime()
    : Infinity;
  const isBuildingByAge = moduleAgeMs < 30_000;
  const hasActiveRun = Boolean((config as any).runId);
  const explicitlyBuilding = config.status === 'queued' || (config.status as string) === 'building';
  // Bail out of the building-loader path the moment the module is archived.
  // Otherwise archiving a freshly-built (< 30s) empty report kept it stuck on
  // the build loader until the parent panel finished re-rendering.
  const isArchived = Boolean(
    (module as any).archived || (module as any).archivedAt || (module as any).archived_at,
  );
  if (
    !isArchived &&
    sortedSections.length === 0 &&
    (hasActiveRun || explicitlyBuilding || isBuildingByAge)
  ) {
    return (
      <div ref={reportRef} className="module module-report xv-scroll" style={theme.variables as React.CSSProperties}>
        <div className="xavior-build-loader">
          <div className="xavior-build-loader__logo-wrap">
            <div className="xavior-build-loader__glow" />
            <img src="/favicon.svg" alt="Xavior" className="xavior-build-loader__logo" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={reportRef} className="module module-report xv-scroll" style={theme.variables as React.CSSProperties}>
      <ReportStatusBar
        message={config.statusMessage}
        isComplete={config.status === 'complete'}
        isError={config.status === 'error'}
        moduleId={module.id}
        publishUrl={config.publish?.url}
        currentTheme={config.theme}
        onThemeChange={handleThemeChange}
        themePickerForceOpen={themePickerOpen}
        onThemePickerClosed={() => setThemePickerOpen(false)}
        onPublished={handlePublished}
        onUnpublished={handleUnpublished}
        onDownload={handleDownload}
      />
      {showRepublishPrompt && (
        <div className="report-republish-prompt">
          <span>Report is published — update live version?</span>
          <button onClick={() => { setShowRepublishPrompt(false); handleRepublish(); }}>Update</button>
          <button onClick={() => setShowRepublishPrompt(false)}>Dismiss</button>
        </div>
      )}
      {theme.fontsUrl && (
        <link rel="stylesheet" href={theme.fontsUrl} />
      )}
      <div className="report-container">
        {sortedSections.map(section => (
          <ReportSection key={section.id} section={section} />
        ))}
      </div>
    </div>
  );
}
